import { useReactTable, getCoreRowModel, flexRender, ColumnDef } from "@tanstack/react-table";
import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/utils/cn";

export type BaseSettingsTableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData, keyof TData>[];
  tableClassName?: string;
  /** The page header and any controls above the table. */
  TopSection?: React.ReactNode;
  onRowClick?: (row: TData) => void;
};

/** A settings list in S's look: a card holding a table whose rows can open the item. */
export function BaseSettingsTable<TData>({
  data,
  columns,
  tableClassName,
  TopSection,
  onRowClick,
}: BaseSettingsTableProps<TData>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {TopSection}
      {/* The card fills the page's height and scrolls itself, so the header row stays visible. */}
      <div className="flex min-h-0 flex-1 flex-col px-3 pb-4 md:px-4">
        <div className="surface-card flex min-h-0 flex-col overflow-hidden bg-card text-card-foreground">
          <Table className={tableClassName} containerClassName="min-h-0 flex-1">
            <TableHeader className="sticky top-0 bg-card">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="h-11 px-4">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                  className={cn("group", onRowClick && "cursor-pointer")}
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        "h-12 px-4",
                        index === 0 && onRowClick && "group-hover:text-primary",
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
